import { tx as translateInterfaceText } from '@/utils/i18n';
import { LanguageSelector } from '@/components/layout/header/LanguageSelector';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomAuth } from "@/contexts/CustomAuthContext";
import { Mail, User, Phone, CreditCard } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { authService } from "@/services/authService";
import { t } from "@/utils/i18n";
import { useLanguage } from "@/hooks/useLanguage";
import {
  AuthLayout,
  AuthCard,
  AuthCardHeader,
  AuthCardFooter,
  TextField,
  PasswordField,
  PrimaryButton,
  HumanizedVisualPanel,
} from "@/components/auth";

import { useLocation } from 'react-router-dom';

type SignupFormData = { name: string; sobrenome: string; email: string; cpf: string; whatsapp: string; password: string, indicator: string, ref: string };
interface perfectPayParameterUserData {
  name: string;
  email: string;
}
let passMatch = ''
let passMatchOriginal = ''
export default function Signup() {

  useLanguage();
  const navigate = useNavigate();
  const { login, refreshUser } = useCustomAuth();
  const signupSchema = z.object({
    name: z.string().min(2, t('nameTooShort')),
    sobrenome: z.string().min(2, t('nameTooShort')),
    email: z.string().email(t('invalidEmailMsg')),
    cpf: z.string().min(11, t('invalidCPF')).max(14, t('invalidCPF')),
    ref: z.string(),
    indicator: z.string(),
    whatsapp: z.string().min(10, t('invalidWhatsApp')),
    password: z.string().min(8, t('passwordTooShort')),
  });

  const [testePassword, setTestPassword] = useState("")
  const [matchPass, setMatchPass] = useState(true)

  const checkPassword = () => {
    if (passMatchOriginal != passMatch) {
      setMatchPass(false)
    } else {
      setMatchPass(true)
    }
  }

  const handleImageClick = () => {
    navigate('/');
  };

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const name = query.get('name')?.trim() ?? '';
  const email = query.get('email')?.trim() ?? '';
  const indicator = query.get('indicator')?.trim() ?? '';
  // Plano e cupom promocional vindos do site de vendas (host-site-2).
  const planParam = query.get('plan')?.trim() ?? '';
  const couponParam = query.get('coupon')?.trim() ?? '';
  const ref = query.get('ref')?.trim() ?? '';

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<SignupFormData>({
    name: name ? name.toString() : "",
    sobrenome: "",
    email: email ? email.toString() : "",
    indicator: indicator ? indicator.toString() : "",
    ref: ref ? ref.toString() : "",
    cpf: "",
    whatsapp: "",
    password: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof SignupFormData, string>>>({});
  const [acceptTerms, setAcceptTerms] = useState(false);

  useEffect(() => {
    (async () => {
      if (email) {
        const api = import.meta.env.VITE_API
        const userExists = await fetch(`${api}usuario/validate-user/byemail/` + email);
        const user = await userExists.json()

        if (user?.success == true) {
          toast.info(t('userAlreadyExists'));
          setTimeout(() => {
            navigate('/');
          }, 5000)
        }
      }
    })()

    const perfectPayParameter = query.get('p')?.trim() ?? '';
    if (perfectPayParameter) {
      try {
        const decodedString = decodeURIComponent(atob(perfectPayParameter).split('').map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const parsedData: perfectPayParameterUserData = JSON.parse(decodedString);
        setFormData(prev => ({ ...prev, ...parsedData }));
      } catch (err) {
        console.error("Erro ao decodificar os dados do link:", err);
      }
    }
  }, [])

  const handleChange = (field: keyof SignupFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value;
    // Format WhatsApp
    if (field === "password") {
      passMatchOriginal = value
    }
    // Format CPF
    if (field === "cpf") {
      value = value.replace(/\D/g, "").slice(0, 11);
      if (value.length > 9) {
        value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, "$1.$2.$3-$4");
      } else if (value.length > 6) {
        value = value.replace(/(\d{3})(\d{3})(\d{1,3})/, "$1.$2.$3");
      } else if (value.length > 3) {
        value = value.replace(/(\d{3})(\d{1,3})/, "$1.$2");
      }
    }

    // Format WhatsApp
    if (field === "whatsapp") {
      value = value.replace(/\D/g, "").slice(0, 11);
      if (value.length > 6) {
        value = value.replace(/(\d{2})(\d{5})(\d{1,4})/, "($1) $2-$3");
      } else if (value.length > 2) {
        value = value.replace(/(\d{2})(\d{1,5})/, "($1) $2");
      }
    }

    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    checkPassword()
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!matchPass) {
      toast.error(t('passwordError'));
      return;
    }

    if (!acceptTerms) {
      toast.error(t('acceptTermsError'));
      return;
    }

    const result = signupSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof SignupFormData, string>> = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof SignupFormData;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Remove formatting from CPF and WhatsApp before sending
      const cleanWhatsapp = formData.whatsapp.replace(/\D/g, "");
      const cleanCpf = formData.cpf.replace(/\D/g, "");

      const sellerId = localStorage.getItem('seller_id');

      const userData = {
        nome: formData.name,
        sobrenome: formData.sobrenome,
        email: formData.email,
        cpf: cleanCpf,
        senha: formData.password,
        telefone: cleanWhatsapp,
        indicator: formData.indicator,
        ref: formData.ref,
        ...(sellerId ? { seller: sellerId } : {}),
      };

      const response = await authService.cadastro(userData);

      if (response?.data?.success) {
        const { token, user, plano, id_Stripe_Client } = response.data;

        // Salva sessão e popula dados do plano via GET /me
        if (token && user) {
          login(user, token, plano ?? undefined, false);
          await refreshUser();
        }

        if (id_Stripe_Client) {
          sessionStorage.setItem('signup_stripe_client_id', id_Stripe_Client);
        }
        // Carrega o cupom/plano promocional pro checkout (persiste na sessão
        // e também vai na URL, pra travar o cupom já na chegada).
        if (couponParam) sessionStorage.setItem('promo_coupon', couponParam);
        if (planParam) sessionStorage.setItem('promo_plan', planParam);
        localStorage.removeItem('seller_id');
        toast.success(t('signupSuccess'));

        const checkoutQuery = new URLSearchParams();
        if (planParam) checkoutQuery.set('plan', planParam);
        if (couponParam) checkoutQuery.set('coupon', couponParam);
        const checkoutUrl = checkoutQuery.toString()
          ? `/checkout?${checkoutQuery.toString()}`
          : '/checkout';
        setTimeout(() => {
          navigate(checkoutUrl);
        }, 500);
      } else {
        console.log("response?.data?.message", response)
        const message: string = (() => {
          if (String(response?.message).includes("Email já cadastrado")) {
            return translateInterfaceText("Email já cadastrado, por favor insira um email diferente, ou recupere sua senha!") as string
          }
          return response?.message as unknown as string
        })()

        toast.error(message || t('signupError'));
      }
    } catch (error: any) {
      console.error("Signup error:", error);
      toast.error(error?.message || t('signupError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout header={<div className="flex justify-end"><LanguageSelector /></div>}
      visualPanel={<HumanizedVisualPanel />}
    >
      <AuthCard>
        <AuthCardHeader
          logo={
            <img
              src="/assets/imgs/logo-azul-e-preta.png"
              alt={translateInterfaceText("HostVSL")}
              className="h-[34px] sm:h-[36px] lg:h-[42px]"
              onClick={handleImageClick}
              style={{ cursor: 'pointer' }}
            />
          }
          title={t('signupTitle')}
          subtitle={t('signupSubtitle')}
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField
            label={translateInterfaceText("Primeiro Nome")}
            type="text"
            placeholder={translateInterfaceText("Primeiro nome")}
            value={formData.name}
            onChange={handleChange("name")}
            error={errors.name}
            icon={<User className="h-4 w-4" />}
            autoComplete="name"
          />

          <TextField
            label={translateInterfaceText("Sobrenome Completo")}
            type="text"
            placeholder={translateInterfaceText("Sobrenome Completo")}
            value={formData.sobrenome}
            onChange={handleChange("sobrenome")}
            error={errors.sobrenome}
            icon={<User className="h-4 w-4" />}
            autoComplete="sobrenome"
          />

          <TextField
            label={translateInterfaceText("E-mail")}
            type="email"
            placeholder="seuemail@dominio.com"
            value={formData.email}
            onChange={handleChange("email")}
            error={errors.email}
            icon={<Mail className="h-4 w-4" />}
            autoComplete="email"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label={translateInterfaceText("CPF")}
              type="text"
              placeholder="000.000.000-00"
              value={formData.cpf}
              onChange={handleChange("cpf")}
              error={errors.cpf}
              icon={<CreditCard className="h-4 w-4" />}
            />

            <TextField
              label={translateInterfaceText("WhatsApp")}
              type="tel"
              placeholder="(00) 00000-0000"
              value={formData.whatsapp}
              onChange={handleChange("whatsapp")}
              error={errors.whatsapp}
              icon={<Phone className="h-4 w-4" />}
              autoComplete="tel"
            />
          </div>

          <PasswordField
            label={t('passwordLabel')}
            placeholder={t('passwordPlaceholder')}
            value={formData.password}
            onChange={handleChange("password")}
            error={errors.password}
            autoComplete="new-password"
          />

          <PasswordField
            label={t('passwordLabelMatch')}
            placeholder={t('passwordPlaceholder')}
            value={testePassword}
            onChange={(e) => {
              setTestPassword(e.target.value)
              passMatch = e.target.value
              checkPassword()
            }
            }
            error={errors.password}
            autoComplete="new-password"
          />
          {!matchPass && <span style={{ color: 'red', fontSize: '10px' }}>{translateInterfaceText("AS senhas não correspondem!")}</span>}
          <label className="flex items-center gap-2 cursor-pointer pt-2">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              className="w-4 h-4 shrink-0 rounded border-[#E6EAF2] accent-[#1F6BFF] focus:ring-[#1F6BFF]/20"
            />
            <span
              className="text-sm"
              style={{ color: '#5B667A' }}
            >
              {translateInterfaceText("Li e aceito os")}{" "}
              <a
                href="https://www.hostvsl.com.br/termos"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: '#1F6BFF' }}
              >
                {translateInterfaceText("Termos de Uso")}</a>{" "}
              e a{" "}
              <a
                href="https://www.hostvsl.com.br/privacidade"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline"
                style={{ color: '#1F6BFF' }}
              >
                {translateInterfaceText("Política de Privacidade")}</a>
            </span>
          </label>

          <div className="pt-[18px]">
            <PrimaryButton type="submit" loading={isLoading} loadingText={translateInterfaceText("Criando conta...")}>
              {translateInterfaceText("Criar conta grátis")}</PrimaryButton>
          </div>
        </form>

        <AuthCardFooter>
          <span style={{ color: '#5B667A' }}>{t('alreadyHaveAccount')} </span>
          <a
            href="/login"
            className="font-semibold transition-colors hover:underline"
            style={{ color: '#1F6BFF' }}
          >
            {t('loginHereLink')}
          </a>
        </AuthCardFooter>
      </AuthCard>
    </AuthLayout>
  );
}
